import { useMemo } from 'react';
import { useRouter } from '@/hooks';
import { Columns, TemplateTypeWithAllTab } from '@/types';
import {
  POI_COLUMNS,
  BLISTER_COLUMNS,
  VIEW_3D_COLUMN,
} from '@/components/Analysis/Insights/InsightsExplorer/PointOfInterests/constants';
import {
  BLISTER_TYPE,
  PIT_TYPE,
  BLISTER_HEIGHT_LABEL,
  PIT_DEPTH_LABEL,
  ALL_POIS_TAB_LABEL,
} from '@/constants';
import { useSetAtom } from 'jotai';
import { cameraTarget } from '@/state';
import { RowData } from '@/components/Analysis/Insights/InsightsExplorer/PointOfInterests/usePoiData';
import * as analysisState from '@/components/Analysis/state';

type UsePoiColumns = {
  selectedPoiTemplate: TemplateTypeWithAllTab;
  type?: string;
  include3DView?: boolean;
};

export const usePoiColumns = ({
  selectedPoiTemplate,
  type,
  include3DView = false,
}: UsePoiColumns) => {
  const setCameraTarget = useSetAtom(cameraTarget);
  const setSelectedAssemblyId = useSetAtom(analysisState.selectedAssemblyId);
  const setSelectedAssemblyName = useSetAtom(analysisState.selectedAssemblyName);
  const router = useRouter();

  const allColumns = useMemo(() => {
    const commonColumnsWithType = [...POI_COLUMNS].filter((column) =>
      type ? column.key !== 'type' : true
    );

    const blisterFields = (() => {
      const label = type?.toLowerCase().includes(BLISTER_TYPE.toLowerCase())
        ? BLISTER_HEIGHT_LABEL
        : PIT_DEPTH_LABEL;

      if (
        type?.toLowerCase().includes(BLISTER_TYPE.toLowerCase()) ||
        type?.toLowerCase().includes(PIT_TYPE.toLowerCase())
      ) {
        return [
          {
            isSortable: true,
            visibility: true,
            key: 'maxHeight',
            columnName: label,
            displayConfig: {
              type: 'NUMBER',
            },
            sortKey: 'blister.maxHeight',
          },

          {
            visibility: true,
            isSortable: false,
            key: 'isGoverning',
            columnName: 'Governing Blister',
            displayConfig: {
              type: 'STRING',
            },
          },
          ...BLISTER_COLUMNS,
        ];
      } else return [];
    })();

    const templateFields = (() => {
      const result = selectedPoiTemplate?.fields
        .map((field) => {
          if (field.defaultVisible) {
            return {
              isSortable: false,
              visibility: true,
              key: field.id,
              columnName: field.name,
              displayConfig: {
                type: 'STRING',
              },
            };
          }
        })
        .filter((column) => !!column);

      if (!include3DView) return result as Columns[];

      const view3dColumn = {
        ...VIEW_3D_COLUMN,
        onCellClick: setSelectedAssemblyId
          ? (event: React.MouseEvent<HTMLDivElement, MouseEvent>, poi: RowData) => {
              event.stopPropagation();
              const { assemblyId, isUncontextualized, centerPoint3d, imageLink, fov, assembly } =
                poi;

              if (!isUncontextualized && centerPoint3d) {
                setCameraTarget({ lookAt: JSON.parse(centerPoint3d), fov });
              }

              setSelectedAssemblyId(assemblyId);
              setSelectedAssemblyName(assembly);
              const isCtrlClick = event.ctrlKey || event.metaKey;
              if (isCtrlClick) window.open(imageLink as string, '_blank');
              else router.push(imageLink.replace(window.location.origin, ''));
            }
          : undefined,
      };

      return [...result, view3dColumn] as Columns[];
    })();

    const combined: Columns[] = [...commonColumnsWithType, ...blisterFields, ...templateFields];

    return { columns: combined, commonColumns: commonColumnsWithType };
  }, [
    type,
    selectedPoiTemplate?.fields,
    include3DView,
    setSelectedAssemblyId,
    setSelectedAssemblyName,
    router,
    setCameraTarget,
  ]);

  const columns = useMemo(() => {
    return selectedPoiTemplate.name === ALL_POIS_TAB_LABEL
      ? allColumns?.commonColumns
      : allColumns?.columns;
  }, [allColumns?.columns, allColumns?.commonColumns, selectedPoiTemplate.name]);

  return columns;
};
